import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { QueueCounterData } from './queue-counter.model';
import { QueueCounterService } from './queue-counter.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';

@Component({
    selector: 'cv-queue-counter',
    templateUrl: './queue-counter.component.html',
    styleUrls: ['./queue-counter.component.scss']
})
export class QueueCounterComponent implements OnInit {
    @ViewChild('queueCounterContainer') containerRef: ElementRef;

    @Input() public moduleManager: ModuleManager;
    @Input() private moduleKey: string; // GUID for the instance of this module.

    public somethingWentWrong = false;

    public data: QueueCounterData = null; // A copy of data returned by loadData will be stored here.
    public radio = 'both';
    private isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    private isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.

    constructor(
        private queueCounterService: QueueCounterService,
        private config: CvConfigurationService,
        private localizationService: CVLocalizationService
    ) { }

    public get IsLoadingData() { return this.isDataLoading; }
    private resizeObserver!: ResizeObserver;

    async ngOnInit() {
        this.moduleManager.showLoader = true;
        // Module Configuration:
        this.moduleManager.setOptions({
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: settings => {
                this.radio = this.config.isCxOne ? settings.radio : 'both';
                this.loadData(false);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.VeryShort,
            onRefresh: isAutoRefresh => {
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading,
        });

        await this.loadLocalizations();
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    getQueueInfo(): number {
        if (this.radio === 'both') {
            return this.data.BothInQueue;
        } else if (this.radio === 'inqueue') {
            return this.data.BothInQueue - this.data.CallbacksInQueue;
        } else if (this.radio === 'callback') {
            return this.data.CallbacksInQueue;
        }
        return 0;
    }
    
    getLongestQueueTime(): string {
        if (this.radio === 'both') {
            return this.data.LongestQueueTimeBoth;
        } else if (this.radio === 'inqueue') {
            return this.data.LongestCallInQueue;
        } else if (this.radio === 'callback') {
            return this.data.LongestQueueTimeCallback;
        }
        return '';
    }

    // Load module's data
    private async loadData(isAutoRefresh: boolean): Promise<void> {
        if (this.isDataLoading)
            return;
        this.isDataLoading = true;
        this.somethingWentWrong = false;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            this.data = await this.queueCounterService.loadQueueCounterData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            this.moduleManager.showLoader = false;
            this.isFirstLoad = false;
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
            this.somethingWentWrong = true;
        } finally {
            this.isDataLoading = false;
        }
    }

    async loadLocalizations() {
        await this.localizationService.GetSection("Queue_Counter");
    }

    getContactsInQueueLocal() {
        return this.localizationService.getCachedText("Queue_Counter", "Contacts_In_Queue", "CONTACTS IN QUEUE");
    }

    getLongestWaitingTimeLocal() {
        return this.localizationService.getCachedText("Queue_Counter", "Longest_Waiting_Time", "Longest waiting time");
    }
}
